import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPut, apiDelete, apiUpload } from '@/lib/api-client';
import { extractTextFromFile } from '@/lib/document-extractor';

interface RAGDocument {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  errorMessage: string | null;
  chunkCount: number;
  isGlobal: boolean;
  createdAt: string;
}

export function RAGDocumentManager() {
  const [documents, setDocuments] = useState<RAGDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ragEnabled, setRagEnabled] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadDocuments();
    loadRAGSettings();
  }, []);

  async function loadRAGSettings() {
    try {
      const data = await apiGet<Record<string, any>>('/api/admin/settings?keys=rag.enabled');
      setRagEnabled(data['rag.enabled']?.enabled === true);
    } catch (err) {
      console.error('Error loading RAG settings:', err);
    }
  }

  async function loadDocuments() {
    try {
      setLoading(true);

      const data = await apiGet<RAGDocument[]>('/api/documents');
      setDocuments(data);
    } catch (err) {
      console.error('Error loading documents:', err);
      setMessage({ type: 'error', text: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  }

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setUploading(true);
      setMessage(null);

      // Validate file type
      const validTypes = [
        'application/pdf',
        'text/plain',
        'text/markdown',
        'text/csv',
        'application/json',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload PDF, DOCX, XLSX, TXT, MD, CSV, or JSON files.');
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File too large. Maximum size is 50MB.');
      }

      // Extract text from document in browser
      const extractedText = await extractTextFromFile(file);

      if (!extractedText || extractedText.trim().length === 0) {
        throw new Error('Could not extract text from document. The file may be empty or unsupported.');
      }

      // Upload via API with extracted text
      const formData = new FormData();
      formData.append('file', file);
      formData.append('text', extractedText);

      await apiUpload('/api/documents/upload', formData);

      setMessage({ type: 'success', text: 'Document uploaded! Processing started...' });
      await loadDocuments();
    } catch (err: any) {
      console.error('Upload error:', err);
      setMessage({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  }, []);

  async function handleToggleRAG() {
    try {
      const newValue = !ragEnabled;

      await apiPut('/api/admin/settings/rag.enabled', {
        value: { enabled: newValue },
      });

      setRagEnabled(newValue);
      setMessage({
        type: 'success',
        text: `RAG ${newValue ? 'enabled' : 'disabled'} successfully!`,
      });
    } catch (err) {
      console.error('Error toggling RAG:', err);
      setMessage({ type: 'error', text: 'Failed to toggle RAG' });
    }
  }

  async function handleDeleteDocument(docId: string) {
    if (!confirm('Are you sure you want to delete this document? This will also delete all its embeddings.')) {
      return;
    }

    try {
      await apiDelete(`/api/documents/${docId}`);

      setMessage({ type: 'success', text: 'Document deleted successfully!' });
      await loadDocuments();
    } catch (err: any) {
      console.error('Delete error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to delete document' });
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getStatusBadge = (status: RAGDocument['processingStatus']) => {
    const badges = {
      pending: { color: 'bg-amber-500/20 text-amber-400', label: 'Pending' },
      processing: { color: 'bg-blue-500/20 text-blue-400', label: 'Processing' },
      completed: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Completed' },
      failed: { color: 'bg-red-500/20 text-red-400', label: 'Failed' },
    };

    const badge = badges[status];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">RAG Document Management</h2>
          <p className="text-slate-400 text-sm">Upload and manage documents for knowledge retrieval</p>
        </div>

        {/* RAG Enable/Disable Toggle */}
        <button
          onClick={handleToggleRAG}
          className={`px-4 py-2 font-medium rounded-lg transition-colors ${
            ragEnabled
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-charcoal-700 hover:bg-charcoal-600 text-slate-300'
          }`}
        >
          RAG: {ragEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Upload Area */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Upload Document</h3>

        <label
          htmlFor="doc-upload"
          className="block border-2 border-dashed border-charcoal-600 rounded-lg p-8 text-center cursor-pointer
                   hover:border-amber-500 hover:bg-amber-500/5 transition-colors"
        >
          <input
            type="file"
            id="doc-upload"
            accept=".pdf,.txt,.md,.csv,.json,.docx,.xlsx,.xls"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            className="hidden"
            disabled={uploading}
          />

          {uploading ? (
            <>
              <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white">Uploading document...</p>
            </>
          ) : (
            <>
              <svg className="w-16 h-16 mx-auto mb-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-lg font-medium text-white mb-2">Drop files or click to browse</p>
              <p className="text-sm text-slate-400">PDF, DOCX, Excel, TXT, MD, CSV, JSON (max 50MB)</p>
            </>
          )}
        </label>
      </div>

      {/* Documents List */}
      <div className="glass-strong rounded-xl overflow-hidden">
        <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700">
          <h3 className="font-semibold text-white">Uploaded Documents ({documents.length})</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
          </div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-400">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-charcoal-700">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 hover:bg-charcoal-800/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-white">{doc.fileName}</h4>
                      {getStatusBadge(doc.processingStatus)}
                      {doc.isGlobal && (
                        <span className="px-2 py-1 text-xs font-medium bg-amber-500/20 text-amber-400 rounded">
                          Global
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>*</span>
                      <span>{doc.chunkCount} chunks</span>
                      <span>*</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>

                    {doc.errorMessage && (
                      <p className="text-xs text-red-400 mt-2">Error: {doc.errorMessage}</p>
                    )}
                  </div>

                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="px-3 py-1.5 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg
                             transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
